/* Imports */
import { createStore } from 'vuex';

/* Modules */
import compare from './compare.js';
import customerGroups from './customerGroups.js';
import filters from './filters.js';
import kickers from './kickers.js';
import pricing from './pricing.js';
import products from './products.js';
import queryParams from './queryParams.js';
import contentfulFeatureFlags from './contentfulFeatureFlags.js';

/* Helpers */
import { getCountryAndStoreCode } from '../utils/mainUtils.js';
import { getProductCompareOrigin } from '../utils/getProductCompareOrigin.js';

export const storeSettings = {
  modules: {
    filters,
    pricing,
    products,
    kickers,
    queryParams,
    compare,
    customerGroups,
    contentfulFeatureFlags,
  },
  state: {
    locale: '',
    storeCode: '',
    countryCode: '',
    categoryKey: '',
    comparePageUrl: '',
    pricingProxyServicesBaseUrl: '',
    wwwBaseUrl: '',
    publicPath: '',
    content: {},
    appIsReady: false,
    isLoading: true,
    translations: [],
  },
  getters: {
    getLocale: (state) => state.locale,
    getStoreCode: (state) => state.storeCode,
    getCountryCode: (state) => state.countryCode,
    getCategoryKey: (state) => state.categoryKey,
    getAppIsReady: (state) => state.appIsReady,
    getIsLoading: (state) => state.isLoading,
    getComparePageUrl: (state) => state.comparePageUrl,
    getPricingProxyServicesBaseUrl: (state) => state.pricingProxyServicesBaseUrl,
    getWwwBaseUrl: (state) => state.wwwBaseUrl,
    getPublicPath: (state) => state.publicPath,
    getContent: (state) => state.content,
    getSeoTitle: (state) => state.content?.seoTitle,
    getTranslations: (state) => state.translations,
  },
  mutations: {
    setLocale: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.locale = value;
    },
    setStoreCode: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.storeCode = value;
    },
    setCountryCode: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.countryCode = value;
    },
    setCategoryKey: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.categoryKey = value;
    },
    setAppIsReady: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.appIsReady = value;
    },
    setIsLoading: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.isLoading = value;
    },
    setComparePageUrl: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.comparePageUrl = value;
    },
    setPricingProxyServicesBaseUrl: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.pricingProxyServicesBaseUrl = value;
    },
    setWwwBaseUrl: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.wwwBaseUrl = value;
    },
    setPublicPath: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.publicPath = value;
    },
    setContent: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.content = value;
    },
    setTranslations: (state, value) => {
      // eslint-disable-next-line no-param-reassign
      state.translations = value;
    },
  },
  actions: {
    loadLocale: ({ commit }, value) => {
      commit('setLocale', value || 'en-US');
    },
    loadStoreCode: ({ commit }, value) => {
      commit('setStoreCode', value);
    },
    loadCountryCode: ({ commit }, value) => {
      commit('setCountryCode', value);
    },
    loadComparePageUrl: ({ commit }, value) => {
      commit('setComparePageUrl', value);
    },
    loadCategoryKey: ({ commit }, value = '') => {
      commit('setCategoryKey', value);
    },
    setAppIsReady: ({ commit }, value) => {
      commit('setAppIsReady', value);
    },
    setIsLoading: ({ commit }, value) => {
      commit('setIsLoading', value);
    },
    loadPricingProxyServicesBaseUrl: ({ commit }, value) => {
      commit('setPricingProxyServicesBaseUrl', value);
    },
    loadWwwBaseUrl: ({ commit }, value) => {
      commit('setWwwBaseUrl', value);
    },
    loadPublicPath: ({ commit }, value) => {
      commit('setPublicPath', value);
    },
    loadContent: ({ commit }, value) => {
      commit('setContent', value);
    },
    async loadInitialState({ commit, dispatch }) {
      const {
        pageContent,
        locale,
        pricingProxyServicesBaseUrl,
        wwwBaseUrl,
        publicPath,
        translations,
      } = window?.content || {};

      // Inject Contentful Feature Flags
      dispatch('fetchContentfulEnvSettings');
      commit('setLocale', locale);
      commit('setContent', pageContent);
      commit('setPricingProxyServicesBaseUrl', pricingProxyServicesBaseUrl);
      commit('setWwwBaseUrl', wwwBaseUrl);
      commit('setPublicPath', publicPath);
      commit('setTranslations', translations);

      // Construct Compare Page Url
      const compareOrigin = getProductCompareOrigin(window?.location?.origin || 'https://www.garmin.com');
      const comparePageUrl = `${compareOrigin}/${locale}/compare/`;
      commit('setComparePageUrl', comparePageUrl);

      const { countryCode, storeCode } = getCountryAndStoreCode(locale);
      commit('setStoreCode', storeCode);
      commit('setCountryCode', countryCode);

      try {
        await dispatch('setCustomerGroups');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(`setCustomerGroups: ${error}`);
      } finally {
        commit('setAppIsReady', true);
      }
    },
  },
};

export default createStore(storeSettings);
